import { getTenantSlugHostError } from "@repo/shared";
import type { TenantInfo } from "@repo/shared/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { OrganizationCard } from "@/components/auth/organization-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "@/components/ui/link";
import { getTenantBySlug, signInWithTenant } from "@/lib/api/auth-login";
import { authClient } from "@/lib/auth-client";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import {
  getPostLoginRedirect,
  isOAuthLogin,
  rememberSsoLoginQuery,
  stripLoginOnlyParams,
} from "@/lib/post-login-redirect";
import { forgetOrgSlug, getRememberedOrgSlug, rememberOrgSlug } from "@/lib/remembered-org";
import { clearSsoLoginMarker, markSsoLogin } from "@/lib/sso-login-marker";
import {
  getTenantHostContext,
  SWITCH_ORGANIZATION_PARAM,
  switchOrganizationUrl,
  tenantUrl,
} from "@/lib/tenant-host";
import { T } from "./login-form.i18n";
import { ssoRefusalMessage } from "./sso-sign-in-error";

export function LoginForm() {
  const [searchParams] = useSearchParams();
  const { userLanguage } = useUserLanguage();
  const t = useT(T);

  // QL-343 — tenant subdomains. On the root domain the tenant step only picks
  // the organization and moves to its host; on a tenant host the organization
  // is the host itself. Disabled (or any other host) keeps the one-origin flow.
  const hostContext = useMemo(getTenantHostContext, []);
  const hostSlug = hostContext.kind === "tenant" ? hostContext.slug : null;
  const subdomainsEnabled = hostContext.kind === "root" || hostContext.kind === "tenant";

  // The login query as it arrived, read before the hints are stripped below.
  // `?switch=1` ("Change organization" on a tenant host) forgets the remembered
  // organization on the root domain so the picker shows instead of redirecting.
  const [initialParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (hostContext.kind === "root" && params.has(SWITCH_ORGANIZATION_PARAM)) forgetOrgSlug();
    return params;
  });

  const [isTenantLoading, setIsTenantLoading] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isSsoLoading, setIsSsoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"tenant" | "credentials">("tenant");
  const [tenantSlug, setTenantSlug] = useState(() => hostSlug ?? getRememberedOrgSlug() ?? "");
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // `?tenant=<slug>` preselects the organization (deep links, AuthGuard
  // forwarding) and `?sso=true` triggers SSO for it right away.
  const urlTenant = searchParams.get("tenant")?.toLowerCase().trim() || null;
  const autoSso = searchParams.get("sso") === "true";
  // Dev prefill: ?tenant=xxx&email=xxx&password=xxx also fills the credentials
  const devEmail = process.env.NODE_ENV === "development" ? searchParams.get("email") : null;
  const devPassword = process.env.NODE_ENV === "development" ? searchParams.get("password") : null;

  // Resolve the organization up front so the tenant step doesn't flash:
  // the host wins, then the URL param, otherwise the remembered organization
  // from a previous visit. "Change organization" on the credentials step forgets it.
  const [isRestoringOrg, setIsRestoringOrg] = useState(
    () => hostSlug !== null || urlTenant !== null || getRememberedOrgSlug() !== null,
  );
  // True only when the credentials step was reached via the remembered
  // organization (returning user), not by entering the slug manually or via URL
  const [isRestoredOrg, setIsRestoredOrg] = useState(false);

  // Continue on the organization's own host, carrying the login query
  // (?redirect=, ?sso=, MCP OAuth params) but not the org hints. Credentials
  // never travel to another host in the URL (history, logs); only the dev
  // quick-login prefill keeps them, and it exists in development builds only.
  const goToTenantHost = (slug: string) => {
    const params = new URLSearchParams(initialParams);
    params.delete("tenant");
    params.delete(SWITCH_ORGANIZATION_PARAM);
    if (process.env.NODE_ENV !== "development") {
      params.delete("email");
      params.delete("password");
    }
    window.location.replace(tenantUrl(slug, params.size > 0 ? `/login?${params}` : "/login"));
  };

  /** True when an organization found by `slug` must be served on its own host. */
  const belongsOnOtherHost = (slug: string) =>
    subdomainsEnabled && slug !== hostSlug && getTenantSlugHostError(slug) === null;

  useEffect(() => {
    // Strip login hints (and dev credentials) from the URL, keeping e.g.
    // ?redirect=. Use replaceState rather than navigate() so searchParams stay
    // stable and don't cancel the in-flight lookup below.
    const stripped = new URLSearchParams(window.location.search);
    // `ssoError`/`ssoRef`/`ssoKind`/`ssoMessage` are read by the login page before
    // this runs; a reload must not re-show them.
    for (const key of [
      "tenant",
      "sso",
      "ssoError",
      "ssoRef",
      "ssoKind",
      "ssoMessage",
      "email",
      "password",
      SWITCH_ORGANIZATION_PARAM,
    ]) {
      stripped.delete(key);
    }
    const cleaned = stripped.size > 0 ? `/login?${stripped.toString()}` : "/login";
    if (window.location.pathname + window.location.search !== cleaned) {
      window.history.replaceState({}, "", cleaned);
    }

    if (devEmail && devPassword) setFormData({ email: devEmail, password: devPassword });
    // A new sign-in starts here; only an SSO redirect below marks it again
    clearSsoLoginMarker();

    // A tenant host only serves its own organization: a hint for another moves there
    if (hostSlug && urlTenant && belongsOnOtherHost(urlTenant)) {
      goToTenantHost(urlTenant);
      return;
    }

    const remembered = hostSlug || urlTenant ? null : getRememberedOrgSlug();
    const slug = hostSlug ?? urlTenant ?? remembered;
    if (!slug) return;

    let cancelled = false;
    let leaving = false;
    getTenantBySlug(slug)
      .then((tenant) => {
        if (cancelled) return;
        if (tenant) {
          // QL-351 — an old slug of a renamed organization resolves to it; carry
          // on with the current slug, which moves an old host to the new one
          const currentSlug = tenant.slug;
          rememberOrgSlug(currentSlug);
          if (belongsOnOtherHost(currentSlug)) {
            leaving = true;
            goToTenantHost(currentSlug);
            return;
          }
          setTenantSlug(currentSlug);
          setTenantInfo(tenant);
          setIsRestoredOrg(remembered !== null);
          setStep("credentials");
        } else if (hostSlug) {
          // The host names an organization that does not exist
          setTenantSlug("");
          setError(t.orgNotFound);
        } else if (remembered) {
          // Slug no longer resolves to an organization — stop remembering it
          forgetOrgSlug();
          setTenantSlug("");
        } else {
          setTenantSlug(slug);
          setError(t.orgNotFound);
        }
      })
      .catch(() => {
        // Lookup failed — fall back to the tenant step with the slug prefilled
        setTenantSlug(slug);
      })
      .finally(() => {
        if (!cancelled && !leaving) setIsRestoringOrg(false);
      });
    return () => {
      cancelled = true;
    };
    // Runs once on mount: the URL is read before it is cleaned above
  }, []);

  // Where to go once a session exists: back to the API's authorize endpoint
  // for MCP OAuth flows, otherwise the requested page (or home).
  const redirectUrl = getPostLoginRedirect(searchParams);

  const handleTenantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isTenantLoading) return;
    setError(null);

    if (!tenantSlug.trim()) {
      setError(t.enterOrgIdentifier);
      return;
    }

    setIsTenantLoading(true);
    try {
      const normalizedSlug = tenantSlug.toLowerCase().trim();
      const tenant = await getTenantBySlug(normalizedSlug);

      if (!tenant) {
        setError(t.orgNotFound);
        return;
      }

      // QL-351 — an old slug resolves to the renamed organization's current one
      rememberOrgSlug(tenant.slug);
      if (belongsOnOtherHost(tenant.slug)) {
        goToTenantHost(tenant.slug);
        return;
      }
      setTenantSlug(tenant.slug);
      setTenantInfo(tenant);
      setIsRestoredOrg(false);
      setStep("credentials");
    } finally {
      setIsTenantLoading(false);
    }
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSigningIn) return;
    setError(null);

    if (!formData.email || !formData.password) {
      setError(t.fillAllFields);
      return;
    }

    setIsSigningIn(true);
    try {
      const result = await signInWithTenant({
        email: formData.email,
        password: formData.password,
        tenantSlug,
      });

      if (!result.success) {
        setError(result.error || t.invalidCredentials);
        return;
      }

      // 2FA-enabled user: no session exists yet, only the two_factor challenge
      // cookie. Send them to the verification page instead of the app (which
      // would bounce back to /login for lack of a session). Keep the query so
      // the verify page can finish an MCP OAuth flow / return to the requested page.
      if (result.twoFactorRedirect) {
        const query = stripLoginOnlyParams(searchParams).toString();
        window.location.href = query ? `/login/verify-2fa?${query}` : "/login/verify-2fa";
        return;
      }

      // Full page reload to ensure clean auth state after login
      window.location.href = redirectUrl;
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleSSOLogin = async () => {
    if (isSsoLoading) return;
    if (!tenantInfo?.ssoProviderId) {
      setError(t.ssoNotConfigured);
      return;
    }

    setError(null);
    setIsSsoLoading(true);

    // Use Better Auth SSO client to sign in
    const toSafeAbsolute = (url: string) => {
      if (!url.startsWith("http")) return `${window.location.origin}${url}`;
      try {
        const target = new URL(url);
        if (target.origin === window.location.origin) return url;
        // An OAuth sign-in (MCP client, QP's own app) resumes at the API's authorize
        // endpoint (getPostLoginRedirect); falling back to this host would drop it.
        if (
          isOAuthLogin(searchParams) &&
          target.origin === new URL(process.env.BUN_PUBLIC_API_URL ?? "").origin &&
          target.pathname === "/api/auth/oauth2/authorize"
        ) {
          return url;
        }
      } catch {}
      return window.location.origin;
    };
    // A failed SSO login lands on /login/sso-error, which cannot carry a query:
    // keep ?redirect= and MCP OAuth params so a retry still completes the flow.
    rememberSsoLoginQuery(searchParams);
    // QL-345 — lets the app warn about an unconfirmed provider id after the round trip
    markSsoLogin(tenantInfo.ssoProviderId);
    try {
      const result = await authClient.signIn.sso({
        providerId: tenantInfo.ssoProviderId,
        callbackURL: toSafeAbsolute(redirectUrl),
        // Failures come back to this host's login page, not the API (QL-348).
        // No query here: better-auth appends `?error=` to it as-is.
        errorCallbackURL: `${window.location.origin}/login/sso-error`,
      });

      if (result.error) {
        clearSsoLoginMarker();
        const { code, message } = result.error as { code?: string; message?: string };
        setError(ssoRefusalMessage(code, userLanguage) ?? (message || t.ssoLoginFailed));
        setIsSsoLoading(false);
      }
      // If successful, user will be redirected to IdP — don't reset loading state
    } catch {
      clearSsoLoginMarker();
      setError(t.ssoLoginFailed);
      setIsSsoLoading(false);
    }
  };

  // ?sso=true: start SSO as soon as the organization is resolved (once)
  const ssoTriggered = useRef(false);
  useEffect(() => {
    if (!autoSso || ssoTriggered.current) return;
    if (step !== "credentials" || !tenantInfo) return;
    ssoTriggered.current = true;
    void handleSSOLogin();
  }, [autoSso, step, tenantInfo]);

  const handleBack = () => {
    forgetOrgSlug();
    // On a tenant host the organization is the host itself: pick another one
    // on the root domain
    if (hostSlug) {
      const current = new URLSearchParams(window.location.search);
      window.location.href = switchOrganizationUrl(
        isOAuthLogin(current) ? stripLoginOnlyParams(current) : undefined,
      );
      return;
    }
    setTenantSlug("");
    setStep("tenant");
    setTenantInfo(null);
    setIsRestoredOrg(false);
    setError(null);
    setFormData({ email: "", password: "" });
  };

  if (isRestoringOrg) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (step === "tenant") {
    return (
      <form onSubmit={handleTenantSubmit} className="space-y-4">
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
        )}

        <div className="space-y-2">
          <Label htmlFor="tenantSlug">
            {t.organizationIdentifier} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="tenantSlug"
            type="text"
            value={tenantSlug}
            onChange={(e) => setTenantSlug(e.target.value)}
            placeholder="your-organization"
            autoFocus
            disabled={isTenantLoading}
          />
          <p className="text-xs text-muted-foreground">{t.enterOrgUniqueId}</p>
        </div>

        <Button type="submit" className="w-full" disabled={isTenantLoading}>
          {isTenantLoading ? t.checking : t.continue}
        </Button>
      </form>
    );
  }

  // Show credentials form with optional SSO button
  return (
    <div className="space-y-4">
      <OrganizationCard name={tenantInfo?.name} isRestored={isRestoredOrg} onChange={handleBack} />

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
      )}

      {/* SSO Button - show if SSO is enabled */}
      {tenantInfo?.ssoEnabled && (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={handleSSOLogin}
            className="w-full"
            disabled={isSsoLoading}
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            {isSsoLoading ? t.redirecting : `${t.continueWith} ${tenantInfo?.ssoName || "SSO"}`}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t.orContinueWithEmail}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Email/Password Form */}
      <form onSubmit={handleCredentialsSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">
            {t.email} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="you@example.com"
            autoFocus={!tenantInfo?.ssoEnabled}
            disabled={isSigningIn}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">
            {t.password} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="••••••••"
            disabled={isSigningIn}
          />
        </div>

        <Button type="submit" className="w-full" disabled={isSigningIn}>
          {isSigningIn ? t.signingIn : t.signIn}
        </Button>

        <div className="text-center">
          <Link
            to={`/forgot-password?tenant=${encodeURIComponent(tenantInfo?.slug ?? tenantSlug)}`}
            className="text-sm text-primary hover:underline"
          >
            {t.forgotPassword}
          </Link>
        </div>
      </form>
    </div>
  );
}
